import { z } from "zod/v4";

import {
  AgenticLogicEvaluationTelemetrySchema,
  AgenticLogicInvocationSchema,
  AgenticLogicResultEnvelopeSchema,
} from "../types";

import {
  AGENTIC_LOGIC_MAX_PREFETCH_READS,
  AgenticLogicSeededReadSchema,
} from "./read-manifest";

/**
 * The trusted per-invocation runtime context the broker mints and hands to a
 * rule tier. Both tiers — the shared template runner and a per-org custom rule
 * Lambda — receive this SAME block, so the SDK runtime they build behaves
 * identically no matter which tier ran.
 *
 * Every field here is broker-supplied. Rule code and template params can never
 * choose the capability, the read function, the seed inputs (`visitorId` +
 * `ruleVersion`), or the deadline.
 */
export const AgenticLogicRuleRuntimeSystemSchema = z
  .object({
    capability: z.string().min(1),
    sdkReadFunctionName: z.string().min(1),
    visitorId: z.string().min(1),
    country: z.string().min(1),
    shopCurrency: z.string().min(1),
    ruleVersion: z.number().int().positive(),
    exhaustiveChains: z.boolean(),
    deadlineAtMs: z.number().int().positive(),
    /**
     * Reads the broker answered from the shared read cache before the rule
     * ran, keyed by request hash (see `AgenticLogicSeededReadSchema`).
     *
     * Optional for ONE compatibility reason: a custom rule's runtime is
     * bundled at publish, and a bundle built before this field parses the
     * system block strictly and would refuse it. The broker therefore sends it
     * only to versions that carry a read manifest, which are exactly the
     * versions bundled with a runtime that reads it.
     */
    seededReads: z
      .array(AgenticLogicSeededReadSchema)
      .max(AGENTIC_LOGIC_MAX_PREFETCH_READS)
      .optional(),
  })
  .strict();

export type AgenticLogicRuleRuntimeSystem = z.infer<
  typeof AgenticLogicRuleRuntimeSystemSchema
>;

export const AgenticLogicRuleLambdaInputSchema = z
  .object({
    system: AgenticLogicRuleRuntimeSystemSchema,
    invocation: AgenticLogicInvocationSchema,
  })
  .strict();

export type AgenticLogicRuleLambdaInput = z.infer<
  typeof AgenticLogicRuleLambdaInputSchema
>;

export const AgenticLogicRuleLambdaResultSchema = z
  .object({
    envelope: AgenticLogicResultEnvelopeSchema,
    telemetry: AgenticLogicEvaluationTelemetrySchema,
  })
  .strict();

export type AgenticLogicRuleLambdaResult = z.infer<
  typeof AgenticLogicRuleLambdaResultSchema
>;
